<template>
	<v-card outlined class="mt-4">
		<!-- Kept lighter than the page-level tabs above it: transparent background,
			 shorter, so the two rows do not read as one hierarchy. -->
		<v-tabs v-model="tab"
				background-color="transparent"
				height="40"
				class="mobile-access-tabs">
			<v-tab>
				<v-icon small class="mr-2">mdi-cellphone</v-icon>
				Devices
			</v-tab>
			<v-tab>
				<v-icon small class="mr-2">mdi-key-variant</v-icon>
				Unlock codes
			</v-tab>
		</v-tabs>
		<v-divider />
		<v-tabs-items v-model="tab">
			<v-tab-item>
				<v-card-text>
					<p class="caption mb-2">
						Every phone that has connected to this project. Blocking a
						device locks it the next time it checks in.
					</p>
					<v-data-table :headers="deviceHeaders"
								  :items="devices"
								  :loading="loading"
								  dense
								  no-data-text="No devices have connected to this project yet.">
						<template v-slot:item.LastSeenAt="{ item }">
							{{ formatDate(item.LastSeenAt) }}
						</template>
						<template v-slot:item.LeaseExpiresAt="{ item }">
							<span :class="{ 'error--text': item.LeaseExpired }">
								{{ formatDate(item.LeaseExpiresAt) }}
							</span>
						</template>
						<template v-slot:item.Status="{ item }">
							<v-chip x-small
									:color="item.Status === 1 ? 'error' : 'success'"
									dark>
								{{ item.Status === 1 ? 'Blocked' : 'Active' }}
							</v-chip>
						</template>
						<template v-slot:item.actions="{ item }">
							<v-btn v-if="item.Status === 1"
								   icon
								   small
								   color="success"
								   title="Unblock device"
								   @click="setDeviceStatus(item, 0)">
								<v-icon small>mdi-cellphone-check</v-icon>
							</v-btn>
							<v-btn v-else
								   icon
								   small
								   color="error"
								   title="Block device"
								   @click="setDeviceStatus(item, 1)">
								<v-icon small>mdi-cellphone-remove</v-icon>
							</v-btn>
						</template>
					</v-data-table>
				</v-card-text>
			</v-tab-item>
			<v-tab-item>
				<v-card-text>
					<div class="d-flex align-center mb-2">
						<p class="caption mb-0">
							Codes let a locked app be reopened in the field with no
							network connection.
						</p>
						<v-spacer />
						<v-btn color="primary" small @click="showCodeDialog = true">
							<v-icon small class="mr-2">mdi-key-plus</v-icon>
							Generate unlock code
						</v-btn>
					</div>
					<v-data-table :headers="codeHeaders"
								  :items="codes"
								  :loading="loading"
								  dense
								  no-data-text="No unlock codes have been generated for this project.">
						<template v-slot:item.Code="{ item }">
							<span class="mobile-access-code">{{ item.Code }}</span>
						</template>
						<template v-slot:item.ExpiresAt="{ item }">
							{{ formatDate(item.ExpiresAt) }}
						</template>
						<template v-slot:item.CreatedAt="{ item }">
							{{ formatDate(item.CreatedAt) }}
						</template>
						<template v-slot:item.Status="{ item }">
							<v-chip x-small :color="codeStatusColor(item)" dark>
								{{ codeStatus(item) }}
							</v-chip>
						</template>
						<template v-slot:item.actions="{ item }">
							<v-btn icon
								   small
								   title="Copy code"
								   @click="copyCode(item)">
								<v-icon small>mdi-content-copy</v-icon>
							</v-btn>
							<v-btn v-if="!item.IsRevoked"
								   icon
								   small
								   color="error"
								   title="Revoke code"
								   @click="revokeCode(item)">
								<v-icon small>mdi-cancel</v-icon>
							</v-btn>
						</template>
					</v-data-table>
				</v-card-text>
			</v-tab-item>
		</v-tabs-items>

		<mobile-access-code-dialog v-if="showCodeDialog"
								   ref="codeDialog"
								   :showDialog="showCodeDialog"
								   :projectId="projectId"
								   @generate="generateCode"
								   @close="showCodeDialog = false" />
	</v-card>
</template>
<script>
	import { mapGetters } from 'vuex';
	import { ProjectManagementActions } from '../../store/actions';
	import { ProjectManagementGetters } from '../../store/getters';
	import MobileAccessCodeDialog from './MobileAccessCodeDialog.vue';
	import ConfirmDialog from '@/components/core/dialogs/confirmDialog';

	export default {
		name: 'MobileAccessAdmin',
		components: { MobileAccessCodeDialog },
		props: {
			projectId: {
				type: String,
				required: true,
			},
		},
		data() {
			return {
				tab: 0,
				showCodeDialog: false,
				codeHeaders: [
					{ text: 'Code', value: 'Code' },
					{ text: 'Unlocks until', value: 'ExpiresAt' },
					{ text: 'Generated', value: 'CreatedAt' },
					{ text: 'Generated by', value: 'CreatedByUserName' },
					{ text: 'Status', value: 'Status', sortable: false },
					{
						text: '',
						value: 'actions',
						sortable: false,
						align: 'end',
					},
				],
				deviceHeaders: [
					{ text: 'Device', value: 'DeviceName' },
					{ text: 'User', value: 'UserName' },
					{ text: 'Platform', value: 'Platform' },
					{ text: 'App version', value: 'AppVersion' },
					{ text: 'Last seen', value: 'LastSeenAt' },
					{ text: 'Locks on', value: 'LeaseExpiresAt' },
					{ text: 'Status', value: 'Status', sortable: false },
					{
						text: '',
						value: 'actions',
						sortable: false,
						align: 'end',
					},
				],
			};
		},
		computed: {
			...mapGetters({
				codes: [ProjectManagementGetters.GET_MOBILE_ACCESS_CODES],
				devices: [ProjectManagementGetters.GET_MOBILE_DEVICES],
				loading: [ProjectManagementGetters.MOBILE_LOADING],
			}),
		},
		mounted() {
			this.loadData();
		},
		watch: {
			projectId() {
				this.loadData();
			},
		},
		methods: {
			loadData() {
				if (!this.projectId) return;
				this.$store.dispatch(
					ProjectManagementActions.LOAD_MOBILE_ACCESS_CODES,
					this.projectId
				);
				this.$store.dispatch(
					ProjectManagementActions.LOAD_MOBILE_DEVICES,
					this.projectId
				);
			},
			generateCode(param) {
				this.$store
					.dispatch(
						ProjectManagementActions.GENERATE_MOBILE_ACCESS_CODE,
						param
					)
					.then((code) => {
						if (code && this.$refs.codeDialog) {
							this.$refs.codeDialog.setGeneratedCode(code);
						}
					});
			},
			revokeCode(item) {
				ConfirmDialog({
					message:
						'Revoking this code stops it unlocking any device that has not already used it. Continue?',
				}).then((confirmed) => {
					if (!confirmed) return;
					this.$store.dispatch(
						ProjectManagementActions.REVOKE_MOBILE_ACCESS_CODE,
						{ id: item.Id, projectId: this.projectId }
					);
				});
			},
			setDeviceStatus(item, status) {
				const message =
					status === 1
						? 'Blocking this device locks Iris Mobile on it the next time it connects. Continue?'
						: 'Unblock this device?';
				ConfirmDialog({ message }).then((confirmed) => {
					if (!confirmed) return;
					this.$store.dispatch(
						ProjectManagementActions.SET_MOBILE_DEVICE_STATUS,
						{ id: item.Id, status, projectId: this.projectId }
					);
				});
			},
			copyCode(item) {
				navigator.clipboard.writeText(item.Code);
			},
			codeStatus(item) {
				if (item.IsRevoked) return 'Revoked';
				if (item.IsExpired) return 'Expired';
				return 'Active';
			},
			codeStatusColor(item) {
				if (item.IsRevoked) return 'error';
				if (item.IsExpired) return 'grey';
				return 'success';
			},
			formatDate(value) {
				if (!value) return '';
				return new Date(value).toLocaleString();
			},
		},
	};
</script>
<style scoped>
	.mobile-access-tabs >>> .v-tab {
		font-size: 0.8125rem;
		letter-spacing: 0;
		text-transform: none;
	}

	.mobile-access-code {
		font-family: monospace;
		letter-spacing: 0.05rem;
	}
</style>
